"use client";

import { cva } from "class-variance-authority";
import { AlertCircle } from "lucide-react";
import type { ElementType, FC, HTMLAttributes, ReactNode } from "react";
import { Fact } from "@/components/ui/fact";
import { cn } from "@/lib/utils";
import { DocumentCardContext, useDocumentCard } from "./document-card.context";

export type DocumentCardVariant = "neutral" | "info" | "warning" | "danger";

export interface DocumentCardProps extends HTMLAttributes<HTMLElement> {
  variant?: DocumentCardVariant;
  as?: ElementType;
}

const Root: FC<DocumentCardProps> = ({
  variant = "neutral",
  as: Tag = "article",
  className,
  children,
  ...props
}) => (
  <DocumentCardContext.Provider value={{ variant }}>
    <Tag
      data-variant={variant}
      className={cn("overflow-hidden surface-card shadow-sm", className)}
      {...props}
    >
      {children}
    </Tag>
  </DocumentCardContext.Provider>
);

const HEADER_VARIANTS = cva("flex", {
  variants: {
    variant: {
      neutral: "items-start gap-3.5 px-4 pt-4",
      info: "items-center gap-3 bg-primary px-4 py-3 text-on-primary",
      warning: "items-center gap-3 bg-feedback-caution-strong px-4 py-3 text-on-accent",
      danger: "items-center gap-3 bg-feedback-danger px-4 py-3 text-on-primary",
    },
  },
});

const HEADER_ICON_VARIANTS = cva(
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg [&>svg]:h-5 [&>svg]:w-5",
  {
    variants: {
      variant: {
        neutral: "bg-bg-tint text-primary",
        info: "bg-on-primary/20 text-on-primary",
        warning: "bg-on-accent/10 text-on-accent",
        danger: "bg-on-primary/20 text-on-primary",
      },
    },
  },
);

interface HeaderProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  className?: string;
}

const Header: FC<HeaderProps> = ({ icon, title, description, badge, className }) => {
  const { variant } = useDocumentCard();
  const striped = variant !== "neutral";

  return (
    <div className={cn(HEADER_VARIANTS({ variant }), className)}>
      {icon && (
        <span aria-hidden className={HEADER_ICON_VARIANTS({ variant })}>
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p
          className={
            striped
              ? "truncate font-display text-h6 font-bold"
              : "text-body-sm font-semibold text-fg-strong"
          }
        >
          {title}
        </p>
        {description && (
          <p className={striped ? "text-body-xs opacity-90" : "mt-0.5 text-body-xs text-fg-muted"}>
            {description}
          </p>
        )}
      </div>
      {badge && <span className="shrink-0">{badge}</span>}
      {variant === "danger" && <AlertCircle data-alert aria-hidden className="h-5 w-5 shrink-0" />}
    </div>
  );
};

interface PartProps {
  className?: string;
  children?: ReactNode;
}

const Body: FC<PartProps> = ({ className, children }) => {
  const { variant } = useDocumentCard();

  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        variant === "neutral" ? "px-4 pt-3 pb-4" : "p-4",
        className,
      )}
    >
      {children}
    </div>
  );
};

const Title: FC<PartProps> = ({ className, children }) => (
  <p className={cn("text-body-sm font-semibold text-fg-strong", className)}>{children}</p>
);

const Fields: FC<PartProps> = ({ className, children }) => (
  <dl className={cn("grid grid-cols-2 gap-x-4 gap-y-2", className)}>{children}</dl>
);

const FIELD_HIGHLIGHT: Record<DocumentCardVariant, string> = {
  neutral: "text-fg-strong",
  info: "text-fg-strong",
  warning: "text-feedback-warning",
  danger: "text-feedback-danger",
};

interface FieldProps extends PartProps {
  label: string;
  /** The value takes the color of the card's variant. */
  highlighted?: boolean;
}

const Field: FC<FieldProps> = ({ label, highlighted = false, className, children }) => {
  const { variant } = useDocumentCard();

  return (
    <Fact
      label={label}
      size="sm"
      className={className}
      valueClassName={
        highlighted ? cn("text-body-sm font-semibold", FIELD_HIGHLIGHT[variant]) : undefined
      }
    >
      {children}
    </Fact>
  );
};

const Actions: FC<PartProps> = ({ className, children }) => (
  <div className={cn("flex flex-wrap gap-2", className)}>{children}</div>
);

Root.displayName = "DocumentCard";
Header.displayName = "DocumentCard.Header";
Body.displayName = "DocumentCard.Body";
Title.displayName = "DocumentCard.Title";
Fields.displayName = "DocumentCard.Fields";
Field.displayName = "DocumentCard.Field";
Actions.displayName = "DocumentCard.Actions";

export const DocumentCard = Object.assign(Root, { Header, Body, Title, Fields, Field, Actions });
